import type { FC } from 'react';
import React, { useContext, useCallback, useEffect } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl-next';

import Tooltip from '@atlaskit/tooltip/Tooltip';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenFilledIcon from '@atlaskit/icon/core/eye-open-filled';

import { WatchDialog } from '@confluence/watch-dialog/entry-points/WatchDialog.tsx';
import { MANAGE_WATCHER_TAB_TYPES } from '@confluence/manage-watcher-dialog/entry-points/manage-watcher-tab-types.ts';
import { WatchButtonPlaceholder } from '@confluence/watch-dialog/entry-points/WatchButtonPlaceholder.js';

import { FeedCardContext } from '../FeedCardContext.ts';
import type { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../../../Feed.tsx';

import { StyledActionButton, ActionButtonWrapper } from './ActionButtonStyles.tsx';

const i18n = defineMessages({
	watch: {
		id: 'feed.card.watch',
		defaultMessage: 'Watch',
		description: 'Text for the watch button in the ellipses dropdown menu',
	},
	unwatch: {
		id: 'feed.card.unwatch',
		defaultMessage: 'Unwatch',
		description: 'Text for the unwatch button in the ellipses dropdown menu',
	},
});

type TriggerProps = {
	isWatching: boolean;
	isSelected: boolean;
	onClick: () => void;
	onMouseOver: () => void;
	triggerProps?: any;
	setIsMenuOpen: (open: boolean) => void;
	contentType: string;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
};

const WatchTriggerComponent: FC<WrappedComponentProps & TriggerProps> = ({
	intl,
	isWatching,
	isSelected,
	onClick,
	onMouseOver,
	triggerProps,
	setIsMenuOpen,
	contentType,
	popularFeedTimeGranularity,
}) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);

	useEffect(() => {
		setIsMenuOpen(isSelected);
	}, [setIsMenuOpen, isSelected]);

	const handleWatchButtonClick = useCallback(async () => {
		onClick();
		fireAnalyticsEvent({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'content-watch-button',
			actionSubjectId: 'feedCardDropdown',
			additionalAttributes: {
				contentType,
				popularFeedTimeGranularity,
			},
		});
	}, [onClick, fireAnalyticsEvent, contentType, popularFeedTimeGranularity]);

	const icon = isWatching ? (
		<EyeOpenFilledIcon label={intl.formatMessage(i18n.unwatch)} />
	) : (
		<EyeOpenIcon label={intl.formatMessage(i18n.watch)} />
	);

	return (
		<Tooltip
			content={<FormattedMessage {...(isWatching ? i18n.unwatch : i18n.watch)} />}
			position="top"
			hideTooltipOnClick
		>
			<StyledActionButton
				onClick={handleWatchButtonClick}
				onMouseOver={onMouseOver}
				appearance="subtle"
				iconBefore={icon}
				isSelected={isSelected}
				{...triggerProps}
			/>
		</Tooltip>
	);
};
const WatchTrigger = injectIntl(WatchTriggerComponent);

type Props = {
	contentId: string;
	setIsMenuOpen: (open: boolean) => void;
	hasCardBeenHovered: boolean;
	contentType: string;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
	followingFeedSortby?: FollowingFeedSortByValue;
};
export const FeedCardWatchAction: FC<Props> = ({
	contentId,
	setIsMenuOpen,
	hasCardBeenHovered,
	contentType,
	popularFeedTimeGranularity,
	followingFeedSortby,
}) => {
	const watchTrigger = useCallback(
		(props: any) => (
			<WatchTrigger
				{...props}
				setIsMenuOpen={setIsMenuOpen}
				contentType={contentType}
				popularFeedTimeGranularity={popularFeedTimeGranularity}
				followingFeedSortby={followingFeedSortby}
			/>
		),
		[setIsMenuOpen, contentType, popularFeedTimeGranularity, followingFeedSortby],
	);

	return (
		<ActionButtonWrapper>
			{hasCardBeenHovered ? (
				<WatchDialog
					contentId={contentId}
					defaultTab={MANAGE_WATCHER_TAB_TYPES.PAGE}
					customTrigger={watchTrigger}
					useShortcut={false}
				/>
			) : (
				<WatchButtonPlaceholder />
			)}
		</ActionButtonWrapper>
	);
};
